import { IconButton } from "@components/button";
import { LastFieldWrapper, RightIconButtonGroup } from "@components/fastEntry/FastEntry.styles";
import { CloseIcon } from "@components/icon";
import { IFormatOptions } from "@odata/OData.utils";
import { isDefined } from "@utils/general";
import React from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import {
    FieldVisibility,
    FormMode,
    GroupedField,
    IconSize,
    LabelStatus,
    NavigationSource,
    Sort,
    TextAlign
} from "../../../enums";
import { TValue } from "../../../global.types";
import { FieldAdditionalData, IAuditTrailData, Model } from "../../../model/Model";
import { RefRegistrar } from "../../../model/RefRegistrar";
import BindingContext, { createPath, IEntity, TEntityKey } from "../../../odata/BindingContext";
import TestIds from "../../../testIds";
import memoizeOneWithParamsCheck from "../../../utils/memoizeOne";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ITableDef } from "../../../views/table/TableView.utils";
import {
    ButtonGroupStyled,
    GroupedKey,
    SmartFieldInHeaderStyled,
    SmartFieldStyled,
    SwitchInHeaderStyled
} from "../../formGroup";
import FormGroup from "../../formGroup/FormGroup";
import { IFormGroupAction } from "../../formGroup/FormGroupActions";
import FieldsWrapper from "../../inputs/field/FieldsWrapper";
import { SwitchType } from "../../inputs/switch/Switch";
import {
    getInfoValue,
    ICustomizationData,
    IFieldDef,
    IFieldInfoProperties,
    isVisible,
    isVisibleByPath,
    TInfoValue,
    TTemporalDialogSettings,
    UnavailableActionStyle
} from "../FieldInfo";
import SmartFastEntryList, {
    ActionType,
    addEmptyLineItem,
    ICustomRowAfterContentArgs,
    ISmartFastEntriesActionEvent,
    ISmartFastEntryListProps
} from "../smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "../smartField/SmartField";
import { getTooltip } from "../smartField/SmartFieldUtils";
import { IActionItem, SmartActionButton } from "./SmartActionButton";
import { getCollapsedGroupStatus } from "./SmartCollapsedBase";
import SmartCollapsedFormGroup from "./SmartCollapsedFormGroup";
import SmartCollapseToggle from "./SmartCollapseToggle";
import SmartFormGroupBase, { ISmartFormGroupBaseProps } from "./SmartFormGroupBase";

export interface IDependentFieldDef {
    from: IFieldDef;
    to: IFieldDef;
    localFrom?: IFieldDef;
    navigateFrom?: NavigationSource;
    value?: TValue;
    // when set, the value from "from" field won't be propagated to "to" field, if its null or undefined
    ignoreNullValue?: boolean;
    formatter?: (value: TValue, args: IFormatOptions) => TValue
}

export interface ISmartFormGroupActionEvent {
    id: string;
    groupId: string;
}

export interface IFormLineItemsDef {
    /** Used for navigation to items without own EntityType */
    collection: string;
    /** Columns displayed in line item */
    columns: IFieldDef[];
    /** Stretch the last field wrapper to the height of its flex row. */
    stretchLastField?: boolean;
    additionalFields?: IFieldDef[];
    // order is required to make sure, backend will always return the items in the right order,
    // and so that we can use it to manually sort the items in AuditTrail
    order: string;
    orderDirection?: Sort;
    preserveOrderOnRemove?: boolean;
    /** Flag if user can manually change the order, e.g. MinorAsset->Items are ordered by transaction date */
    canReorder?: TInfoValue<boolean>;
    /** Indicates whether first item is marked as primary. It displays colored background with title. */
    containsPrimary?: boolean;
    /* Indicates whether the content is collapsible, so only first item is visible at first */
    isCollapsible?: boolean;
    /* Indicates whether the last item can be removed (remove button is not disabled). Disabled by default.
       Last item can be removed on "Type B" line items, which allow to add items from external source (e.g. editable window) */
    isTheOnlyItemRemovable?: boolean;
    /* Indicates if there should be rendered switch in group header, which removes all items at once / adds first item */
    isGroupToggleable?: boolean;
    /* Indicates whether whole lineItems group is readonly (not every single line) */
    isReadOnly?: TInfoValue<boolean>;
    /* Indicates whether particular line item can be removed (so remove action icon is displayed for it) */
    isItemCloneable?: TInfoValue<boolean>;
    isItemRemovable?: TInfoValue<boolean>;
    unavailableActionStyle?: UnavailableActionStyle;
    isItemVisible?: TInfoValue<boolean>;
    isItemDisabled?: TInfoValue<boolean>;
    /*Indicates whether to render checkbox before the FastEntry item. Used to select items for custom action.*/
    isItemSelectable?: TInfoValue<boolean>;
    isDisabled?: TInfoValue<boolean>;
    /* Indicates whether FastEntry list should show add button and add items */
    canAdd?: TInfoValue<boolean>;
    /* Whether the add button should be disabled */
    isAddDisabled?: TInfoValue<boolean>;
    isAddButtonTransparent?: boolean;
    /** For single line only one label line is used. True by default. **/
    useLabelWrapping?: TInfoValue<boolean>;
    /** Never virtualize items */
    disableVirtualization?: TInfoValue<boolean>;
    /** Custom content rendered after each line item, e.g. for sublists, detail or any content related to one fast entry. */
    customRowAfterContent?: (args: ICustomRowAfterContentArgs) => React.ReactNode;

    /** Display line number in the beginning of each row*/
    showLineNumbers?: boolean;

    /** Adds icon which will open extended group actions. So far, only used for custom line items actions.*/
    actions?: TSmartFormGroupAction[];
    /** Adds custom buttons next to the "Add" button. Fires "onLineItemsAction" event, like other actions/customAction */
    customActionButtons?: IActionItem[];
    disableFieldsOnActiveAction?: boolean;
    /** Renders custom items summary next to the "Add" button. */
    itemsSummaryRenderer?: TInfoValue<React.ReactElement>;
    /** Visibility is controlled via variant management/config dialog */
    shouldShowItemsSummary?: boolean;
    /** We render read only fast entry lists in a standard way, just in case we explicitly set it to "false", FEF is rendered as a table.
     * Note, not all features are supported, so use it only when you want different behavior */
    renderAsFastEntryForReadOnly?: boolean;

    /** Dialog settings for whole temporal line item */
    temporalDialog?: TTemporalDialogSettings;

    // filter some items from the collection -> may not work in forms well with adding / ordering. Use on static collections only.
    filterItems?: (storage: FormStorage, id: string, items: IEntity[]) => IEntity[];

    /** Custom renderer for collections */
    render?: (args: IGroupRenderFn) => React.ReactElement;

    // actions for line items, which are rendered at the end of each row as full size buttons with icons.
    // They fire line action event, like other actions/customAction, they also have error state etc. for more information
    // see zeplin specification of fast entry
    lineItemActions?: ILineItemAction[];
}


export type TFormTable =
    Omit<ITableDef, "columnDefinition" | "columns" | "id">
    & {
    id: string;
    endPoint?: string;
    columns: IFieldDef[];
    isSimplified?: boolean;
    preventAutoReset?: boolean;
};

export type TSmartFormGroupAction = (Omit<IFormGroupAction, "isDisabled"> & {
    confirmLabel: string;
    isDisabled?: TInfoValue<boolean>;
    isVisible?: TInfoValue<boolean>;
});

export interface ILineItemAction {
    id: string;
    label: string;
    isDisabled?: TInfoValue<boolean>;
    isVisible?: TInfoValue<boolean>;
    iconName: string;
}

export enum FormGroupType {
}

export interface IFormGroupDef {
    id: string;

    title?: TInfoValue<string>;
    tooltip?: ((storage?: FormStorage) => React.ReactElement) | string;
    showGroupDivider?: boolean;

    customizationData?: ICustomizationData;
    /** Removable group renders remove icon. */
    isRemovable?: TInfoValue<boolean>;
    isVisible?: TInfoValue<boolean>;
    /** If set to true, SmartFormGroup calls isVisible callback and if return "true", shows
     * group even if there are no fields visible (e.g. to show Title, etc...) */
    forceVisibilityWithoutFields?: boolean;

    tabs?: IFormGroupDef[];

    /** Defines group of fields that are together in one row */
    rows?: (IFieldDef[])[];
    afterContent?: IFieldDef[];
    /** If defined, multiple groups are rendered, one for each item in given collection.
     * Other parameters (title, rows...) are reused in the groups. */
    collection?: string;
    parentCollections?: string[]; // virtualized form can structure collections for visibility condition, etc...
    /** In case we want to render only one item from collection */
    collectionKey?: TInfoValue<TEntityKey>;
    /** If collection is used, collectionOrder is required to make sure, backend will always return the items in the right order,
     * and so that we can use it to manually sort the items in AuditTrail
     * */
    collectionOrder?: string;
    /** Use if you need different header for every item in collection  */
    collectionItemTitle?: string | ((index: number, item: IEntity, storage: FormStorage) => string);
    /** Defines collection of items rendered as line items */
    lineItems?: IFormLineItemsDef;
    /** todo: remove this exception
     * Sometimes, we use group without label to place collapsedRows on other than last place in a group.
     * In that case, we want the group to have same bottom padding as fields would otherwise have between themselves,
     * instead of the bigger gap that we have between groups. */
    isSmallBottomMargin?: boolean;
    /**
     * If group is toggleable, this should be id of property,
     * which will be set to true / false (switch is displayed in header when configured),
     * Note: property should be added to additionalProperties, so it's definition is correctly load - FormStorage
     *       doesn't work directly with this property */
    togglePropPath?: string;
    // simple flag to indicate if group is collapsible - primary use in VirtualizedForm to collapse collection groups.
    isCollapsible?: boolean;

    // only main table supports customizing (columnDefinition)
    // tabs in tables should have all their settings directly in columns
    table?: TFormTable;

    render?: (storage: FormStorage, group: IFormGroupDef) => React.ReactElement;

    /* List of action items displayed at the end of the group. Click on the item emits event on storage */
    actions?: IActionItem[];
    /** Alignment of the actions button group. Right by default. */
    actionsAlignment?: TextAlign;
}

interface IProps extends ISmartFormGroupBaseProps {
    isInEdit?: boolean;

    onExpand?: (isExpanded: boolean, id: string) => void;
    onRemove?: (bindingContext: BindingContext) => void;
    onLineItemsChange: (args: ISmartFieldChange) => void;
    onLineItemsAction: (args: ISmartFastEntriesActionEvent) => void;
    onGroupAction?: (args: ISmartFormGroupActionEvent) => void;
}

export interface IGroupRenderFn {
    storage: Model;
    props: ISmartFormGroupRenderProps;
    groupId: string;
}

export interface ISmartFormGroupRenderProps extends Pick<IFormLineItemsDef, "useLabelWrapping" | "showLineNumbers" | "isTheOnlyItemRemovable" | "isCollapsible" | "containsPrimary" | "customActionButtons"
    | "isItemVisible" | "isItemRemovable" | "isItemCloneable" | "isItemDisabled" | "isItemSelectable" | "order" | "orderDirection" | "preserveOrderOnRemove" | "canReorder" | "canAdd" | "columns"
    | "disableVirtualization" | "disableFieldsOnActiveAction" | "isDisabled" | "isAddDisabled" | "isAddButtonTransparent" | "isGroupToggleable">, Pick<IProps, "storage"> {
    groupId: string;
    isReadOnly: boolean;
    customAction: TSmartFormGroupAction,
    onCustomActionFinish: () => void;
    customFooterContent: React.ReactNode;
    isLight: boolean;
    bindingContext: BindingContext;
    onBlur: (args: ISmartFieldBlur) => Promise<void>;
    onChange: (args: ISmartFieldChange) => void;
    onAction: (args: ISmartFastEntriesActionEvent) => void;
}

export default class SmartFormGroup extends SmartFormGroupBase<IProps> {
    static contextType = AppContext;

    get actionsAlignment(): TextAlign {
        return this.props.def.actionsAlignment;
    }

    handleExpand = (isExpanded: boolean, id: string): void => {
        this.props.onExpand?.(isExpanded, id);
        this.forceUpdate();
    };

    renderField = (rows: IFieldDef[], opts: {
        isInOpenGroup: boolean,
        isCreating?: boolean,
        collapseToggle?: React.ReactElement,
        isLastRow?: boolean
    }): React.ReactElement[] => {
        let multiArray: React.ReactElement[] = [];
        const array: React.ReactElement[] = [];
        let currentArray = array;

        const { isInOpenGroup, isCreating, collapseToggle, isLastRow } = opts;

        const _isGroupedFieldStart = () => {
            return [GroupedField.MultiStart, GroupedField.NoWrapStart, GroupedField.WrappedMultiStart].includes(groupedField);
        };

        const _isGroupFieldEnd = () => {
            return [GroupedField.MultiEnd, GroupedField.NoWrapEnd, GroupedField.WrappedMultiEnd].includes(groupedField);
        };

        const isRemovable = getInfoValue(this.props.def, "isRemovable", {
            storage: this.props.storage
        });

        const _addField = (field: IFieldDef, bc: BindingContext, isLast?: boolean) => {
            const isMultiMiddle = !groupedField && isMultiField;
            const addGroupedToggle = isLast && collapseToggle;

            if (_isGroupedFieldStart() || (addGroupedToggle && !isMultiField)) {
                multiArray = [];
                currentArray = multiArray;
                if ([GroupedField.MultiStart, GroupedField.WrappedMultiStart].includes(groupedField)) {
                    isMultiField = true;
                }
            }

            const fieldDef: IFieldInfoProperties = this.props.isSkeleton ? this.getFieldDefinition(field) : null;

            const smartField =
                <SmartFieldStyled
                    isSkeleton={this.props.isSkeleton}
                    _isLast={isLast}
                    key={bc.toString()}
                    bindingContext={bc}
                    style={field.customFieldStyle}
                    storage={this.props.storage}
                    fieldProps={{
                        isSharpRight: [GroupedField.MultiStart, GroupedField.WrappedMultiStart].includes(groupedField) || isMultiMiddle,
                        isSharpLeft: [GroupedField.MultiEnd, GroupedField.WrappedMultiEnd].includes(groupedField) || isMultiMiddle,
                        isLight: !isInOpenGroup && !isCreating
                    }}
                    onConfirm={this.handleConfirm}
                    onCancel={this.handleCancel}
                    onFieldStateChange={this.handleFieldStateChange}
                    onTemporalChange={this.handleTemporalChange}
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    fieldDef={fieldDef}
                />;

            if (isLast && isLastRow && isRemovable) {
                currentArray.push(<LastFieldWrapper>
                    {smartField}
                    <RightIconButtonGroup data-testid={TestIds.ButtonGroup}>
                        <IconButton onClick={this.handleGroupRemove}
                                    isDecorative
                                    title={this.props.storage.t("Common:General.Remove")}>
                            <CloseIcon width={IconSize.S} height={IconSize.S}/>
                        </IconButton>
                    </RightIconButtonGroup>
                </LastFieldWrapper>);
            } else {
                currentArray.push(smartField);
            }

            if (addGroupedToggle) {
                currentArray.push(collapseToggle);
            }

            if ((_isGroupFieldEnd() || addGroupedToggle) && multiArray?.length > 0) {
                const shouldAddGroup = groupedField !== GroupedField.WrappedMultiEnd || (addGroupedToggle && multiArray.length <= 2);
                if (shouldAddGroup) {
                    array.push(<GroupedKey key={"grouped_" + field.id}>{multiArray}</GroupedKey>);
                } else if (addGroupedToggle) {
                    // wrapped multigroup with toggle -> just group the toggle with last field,
                    // rest will be added with possibility to wrap
                    const lastFieldIdx = multiArray.length - 2;
                    array.push(...multiArray.slice(0, lastFieldIdx));
                    array.push(<GroupedKey
                        key={"grouped_" + field.id}>{multiArray.slice(lastFieldIdx, multiArray.length)}</GroupedKey>);
                } else {
                    // no grouping when using WrappedMultiEnd, just use sharp edges on single fields
                    array.push(...multiArray);
                }
                currentArray = array;
                isMultiField = false;
                multiArray = [];
            }
        };
        let groupedField: GroupedField;
        let isMultiField = false;
        for (let i = 0; i < rows.length; i++) {
            const field = this.getFieldDefinition(rows[i]);
            const bc = this.props.bindingContext.navigate(field.id);

            groupedField = getInfoValue(field, "groupedField", {
                bindingContext: bc,
                storage: this.props.storage
            });

            if (field.fieldVisibility && this.props.storage.formMode !== FormMode.AuditTrail) {
                if ((this.props.isInEdit && field.fieldVisibility === FieldVisibility.CreateOnly) ||
                    (!this.props.isInEdit && field.fieldVisibility === FieldVisibility.EditOnly)) {
                    continue;
                }
            }

            // use props bindingContext, not storage bindingContext
            // group can be navigated inside the collection
            if (bc.getParent()?.isCollection() && !this.props.isSkeleton) {
                const path = bc.getPath();
                const values = this.props.storage.getValue(bc.getParent()) || [];
                let newItemsCount = 0;
                for (const expandedField of values) {
                    const key = bc.getKeyPropertyName();
                    const isNew = !expandedField[key];
                    const fieldBc = bc.getParent().addKey(expandedField[key] || newItemsCount++, isNew).navigate(path);

                    _addField(field, fieldBc);
                }

                continue;
            }

            _addField(field, bc, i === rows.length - 1);
        }

        return array;
    };

    // rowsTop -- groups.rows has nested array
    renderFields = (rowsTop: (IFieldDef[])[], isInOpenGroup: boolean, isCreating = false): React.ReactElement[] => {
        return (rowsTop || []).map((rows, index) => {
            if (!rows || !rows.length) {
                return null;
            }
            const lastField = this.getFieldDefinition(rows[rows.length - 1]);

            const {
                hasVisibleCollapsedFields,
                hasEnabledCollapsedFields
            } = this.getCollapsedFieldsVisibility(lastField);

            const collapseToggle = hasVisibleCollapsedFields && (lastField.collapsedRows || []).length > 0 && isInOpenGroup && (
                <SmartCollapseToggle storage={this.props.storage}
                                     key={`collapseGroupToggle-${lastField.id}`}
                                     def={lastField}
                                     isCollapsedGroupDisabled={!hasEnabledCollapsedFields}
                                     isSkeleton={this.props.isSkeleton}
                                     onExpand={this.handleExpand}/>
            );

            const isRemovable = getInfoValue(this.props.def, "isRemovable", {
                storage: this.props.storage
            });
            const isLastRow = index === rowsTop.length - 1;

            return (<React.Fragment key={index}>
                <FieldsWrapper style={{ width: "100%", maxWidth: "100%" }} isSkeleton={this.props.isSkeleton}>
                    {this.renderField(rows, { isInOpenGroup, isCreating, collapseToggle, isLastRow })}
                    {!isRemovable && this.props.def.afterContent && isLastRow &&
                        this.renderField(this.props.def.afterContent, {
                            isInOpenGroup,
                            isCreating
                        })
                    }
                </FieldsWrapper>
                {isRemovable && this.props.def.afterContent && isLastRow &&
                    <FieldsWrapper style={{ width: "100%", maxWidth: "100%" }} isSkeleton={this.props.isSkeleton}>
                        {this.renderField(this.props.def.afterContent, { isInOpenGroup })}
                    </FieldsWrapper>
                }
                {this.renderCollapsedGroup(lastField, hasVisibleCollapsedFields && !this.props.isSkeleton, !hasEnabledCollapsedFields)}
            </React.Fragment>);
        });
    };

    getCollectionBindingContext = memoizeOneWithParamsCheck((lineItemsDef?: IFormLineItemsDef) => {
        const collection = lineItemsDef?.collection ?? this.props.def.lineItems?.collection;
        return this.props.bindingContext.navigate(collection);
    }, (lineItemsDef?: IFormLineItemsDef) => [this.props.bindingContext.toString(), lineItemsDef ? lineItemsDef?.collection : this.props.def.lineItems?.collection]);

    // toggle switch of toggleable line items
    handleGroupToggle = (checked: boolean): void => {
        const { def, storage } = this.props;
        const { id, lineItems } = def;
        const bindingContext = this.getCollectionBindingContext();
        const affectedItems = storage.getValue(bindingContext) || [];

        // store temporarily removed items to storage, so it's bound to the entity
        const localContextPath = BindingContext.localContext(createPath(id, lineItems.collection, "removedItems"));

        if (checked) {
            // user turns line items group on. It should return back previous items (if set) or one empty item
            const removedItems = storage.getValueByPath(localContextPath) || [];
            if (!removedItems.length) {
                // add new empty item
                const newItem = addEmptyLineItem({
                    storage,
                    bindingContext,
                    newItemsCount: 1,
                    index: 1,
                    order: lineItems.order,
                    columns: lineItems.columns,
                    context: this.context
                });
                removedItems.push(newItem);
            }
            this.props.onLineItemsAction?.({
                bindingContext,
                actionType: ActionType.Add,
                groupId: id,
                items: removedItems,
                affectedItems: removedItems
            });
        } else {
            // closes the group, removes all items, saves them for later restoration if needed
            this.props.onLineItemsAction?.({
                bindingContext,
                actionType: ActionType.Remove,
                groupId: id,
                items: [],
                affectedItems
            });
            // save removed items for later restoration
            storage.setValueByPath(localContextPath, affectedItems);
        }
    };

    renderLineItems = (collection: IFormLineItemsDef): React.ReactElement => {
        const { storage } = this.props;
        const bindingContext = this.getCollectionBindingContext(collection);
        const { selectedOption } = storage.getGroupStatus(this.props.def.id);

        const customAction = selectedOption ? this.props.def.lineItems.actions.find(action => action.id === selectedOption) : null;

        // todo: do we want to support table in audit trail?? If so, remove the last condition above and prepare audit
        //  trail to render changes in table.
        const isAuditTrail = storage.formMode === FormMode.AuditTrail;

        const isReadOnly = !isAuditTrail && (storage.isReadOnly || getInfoValue(collection, "isReadOnly", {
            storage,
            data: storage.data.entity
        }));

        const useLabelWrapping = getInfoValue(collection, "useLabelWrapping", { storage, bindingContext });

        const commonProps: Partial<ISmartFastEntryListProps | ISmartFormGroupRenderProps> = {
            groupId: this.props.def.id,
            stretchLastField: collection.stretchLastField,
            useLabelWrapping: useLabelWrapping !== false,
            showLineNumbers: !selectedOption && collection.showLineNumbers,
            isCollapsible: collection.isCollapsible,
            isReadOnly: isReadOnly,
            renderAsFastEntryForReadOnly: collection.renderAsFastEntryForReadOnly ?? !!collection.temporalDialog, /* to support temporal values, we can't render the FEF as a table */
            containsPrimary: collection.containsPrimary,
            disableVirtualization: collection.disableVirtualization,
            customAction: customAction,
            onCustomActionFinish: this.handleCustomActionFinish,
            customActionButtons: collection.customActionButtons,
            disableFieldsOnActiveAction: collection.disableFieldsOnActiveAction,
            customFooterContent: (collection.shouldShowItemsSummary !== false) && getInfoValue(collection, "itemsSummaryRenderer", {
                bindingContext,
                storage: this.props.storage,
                context: this.context
            }),
            isItemVisible: collection.isItemVisible,
            isItemRemovable: collection.isItemRemovable,
            isItemCloneable: collection.isItemCloneable,
            isItemDisabled: collection.isItemDisabled,
            isItemSelectable: collection.isItemSelectable,
            isDisabled: collection.isDisabled,
            lineItemActions: collection.lineItemActions,
            isLight: !!selectedOption,
            storage: this.props.storage,
            bindingContext,
            // when ordered by Id, only use it to fetch from backend, but ignore it here.
            // it would cause problems in SmartFastEntry list, because Id would collide with new items #id
            order: collection.order === "Id" ? null : collection.order,
            orderDirection: collection.orderDirection,
            preserveOrderOnRemove: collection.preserveOrderOnRemove,
            canReorder: !isAuditTrail && !selectedOption && (collection.canReorder ?? true),
            canAdd: collection.canAdd ?? true,
            isAddDisabled: collection.isAddDisabled,
            isAddButtonTransparent: collection.isAddButtonTransparent,
            isTheOnlyItemRemovable: collection.isTheOnlyItemRemovable,
            unavailableActionStyle: collection.unavailableActionStyle,
            isGroupToggleable: collection.isGroupToggleable,
            columns: collection.columns,
            onBlur: this.handleBlur,
            onChange: this.props.onLineItemsChange,
            onAction: this.props.onLineItemsAction
        };

        if (collection.render) {
            const groupRenderProps = commonProps as ISmartFormGroupRenderProps;

            return <RefRegistrar
                bindingContext={bindingContext}
                storage={this.props.storage}>
                {() => {
                    return collection.render({
                            storage: this.props.storage,
                            props: groupRenderProps,
                            groupId: this.props.def.id
                        }
                    );
                }}
            </RefRegistrar>;
        }

        return (
            <SmartFastEntryList {...(commonProps as unknown as ISmartFastEntryListProps)}
                                customRowAfterContent={collection.customRowAfterContent}
                                filterItems={collection?.filterItems?.bind(null, storage, this.props.def.id)}/>
        );
    };

    getVisibleActions(): IActionItem[] {
        const actions = this.props.def.actions || [];
        const { storage } = this.props;
        return actions.filter(action => {
            const { isDisabled, ...info } = action;
            return isVisible({ info, storage, context: this.props.storage.context });
        });
    }

    handleCustomButtonAction = (id: string): void => {
        this.props.onGroupAction?.({ id, groupId: this.props.def.id });
    };

    isFormGroupOptionDisabled = (action: TSmartFormGroupAction): boolean => {
        if ((typeof action.isDisabled === "boolean" && action.isDisabled) || this.props.bindingContext.isNew()) {
            return true;
        }

        return getInfoValue(action, "isDisabled", {
            bindingContext: this.getCollectionBindingContext(),
            storage: this.props.storage,
            context: this.context
        });
    };

    getFormGroupOptions = () => {
        if (this.props.storage.isReadOnly || this.props.storage.formMode === FormMode.AuditTrail) {
            return null;
        }

        return this.props.def.lineItems?.actions?.filter?.(action => {
            const isVisible = getInfoValue(action, "isVisible", {
                bindingContext: this.getCollectionBindingContext(),
                storage: this.props.storage,
                context: this.context
            });
            return isVisible !== false;
        })?.map((action) => {
            return {
                ...action,
                isDisabled: this.isFormGroupOptionDisabled(action)
            };
        });
    };

    getTitle = (): string => {
        if (isDefined(this.props.title)) {
            return this.props.title;
        }
        return getInfoValue(this.props.def, "title", {
            storage: this.props.storage
        });
    };

    renderActions = (visibleActions: IActionItem[]): React.ReactElement => {
        if (!visibleActions.length || this.props.storage.formMode === FormMode.AuditTrail || this.props.isSimple) {
            return null;
        }
        return (
            <ButtonGroupStyled align={this.actionsAlignment ?? TextAlign.Right}>
                {visibleActions.map(action => (
                    <SmartActionButton key={action.id} storage={this.props.storage} action={action}
                                       onClick={this.handleCustomButtonAction}/>))}
            </ButtonGroupStyled>
        );
    };

    getFixedHeaderFieldProps = memoizeOneWithParamsCheck(() => ({
        labelStatus: LabelStatus.Removed,
        hasPadding: false
    }));

    renderAdditionalHeaderContent = (auditTrailData: IAuditTrailData): React.ReactElement => {
        const { togglePropPath, lineItems } = this.props.def;

        if (togglePropPath) {
            const { storage } = this.props;
            const bc = storage.data.bindingContext.navigate(togglePropPath);
            return (
                <SmartFieldInHeaderStyled key={bc.toString()}
                                          fieldProps={this.getFixedHeaderFieldProps()}
                                          bindingContext={bc}
                                          storage={storage}
                                          onConfirm={this.handleConfirm}
                                          onCancel={this.handleCancel}
                                          onTemporalChange={this.handleTemporalChange}
                                          onChange={this.handleChange}
                                          onBlur={this.handleBlur} />
            );
        }
        if (lineItems?.isGroupToggleable) {
            const { storage } = this.props;
            const items = storage.getValueByPath(lineItems.collection) as IEntity[];
            const hasLineItems = !!items?.filter(item => {
                // audit trail adds fake items with only Id, so we need to filter them out, otherwise the switch will be always on
                const { Id, ...rest } = item;
                return Object.keys(rest)?.length > 0;
            })?.length;
            return (
                <SwitchInHeaderStyled checked={hasLineItems}
                                      auditTrailData={auditTrailData}
                                      onChange={this.handleGroupToggle}
                                      isReadOnly={storage.isReadOnly}
                                      isDisabled={storage.isDisabled}
                                      type={SwitchType.YesNo}/>
            );
        }
        return null;
    };

    handleGroupRemove = (): void => {
        this.props.onRemove?.(this.props.bindingContext);
    };

    handleOptionClick = (optionId: string): void => {
        this.props.storage.setGroupStatus({ selectedOption: optionId }, this.props.def.id);
        this.forceUpdate();
    };

    handleCustomActionFinish = (): void => {
        this.props.storage.setGroupStatus({ isMenuOpened: false, selectedOption: null }, this.props.def.id);
        this.forceUpdate();
    };

    handleOptionsButtonToggle = (): void => {
        const { storage } = this.props;
        const groupId = this.props.def.id;
        const { isMenuOpened } = storage.getGroupStatus(groupId);
        storage.setGroupStatus({ isMenuOpened: !isMenuOpened }, groupId);
        this.forceUpdate();
    };

    renderCollapsedGroup(def: IFieldDef, showCollapsedGroup = true, isCollapsedGroupDisabled = false): React.ReactElement {
        if (!def?.collapsedRows?.length) {
            return null;
        }

        const status = getCollapsedGroupStatus(this.props.storage, def);
        const isCreating = status?.isCreating;

        return showCollapsedGroup && (
            <SmartCollapsedFormGroup storage={this.props.storage}
                                     def={def}
                                     hasEditableFields={!isCollapsedGroupDisabled}
                                     onExpand={this.handleExpand}>
                {this.renderFields(def.collapsedRows, false, isCreating)}
            </SmartCollapsedFormGroup>
        );
    }

    render() {
        const { storage, def } = this.props;
        let auditTrailData;
        const isAuditTrail = storage.formMode === FormMode.AuditTrail;
        if (isAuditTrail) {
            auditTrailData = storage.getAdditionalFieldData(this.props.bindingContext, FieldAdditionalData.AuditTrailData);
        }

        const hasVisibleFields = this.props.isSkeleton ? true : this.getFieldsVisibility();
        const visibleActions = this.props.isSkeleton ? [] : this.getVisibleActions();

        // Group can force visibility via configuration
        const forceGroupVisibility = !storage.isReadOnly && def.forceVisibilityWithoutFields && getInfoValue(this.props.def, "isVisible", { storage }) === true;
        // todo: how we can handle this better for audit trail? Do we want to show all fields or only fields, which are
        //  visible at least in one form??
        if (!this.props.isSkeleton && !hasVisibleFields && !isAuditTrail && !visibleActions.length && !forceGroupVisibility) {
            return null;
        }
        const { isMenuOpened, selectedOption } = storage.getGroupStatus(this.props.def.id);
        const isGroupToggled = isAuditTrail ? true
            : (def.togglePropPath && isVisibleByPath(storage, def.togglePropPath) ? !!storage.getValue(storage.data.bindingContext.navigate(def.togglePropPath)) : true);

        return (
            <FormGroup id={this.props.def.id}
                       title={this.getTitle()}
                       auditTrailData={auditTrailData}
                       isSimple={this.props.isSimple}
                       isSkeleton={this.props.isSkeleton}
                       tooltip={getTooltip(this.props.def.tooltip, storage)}
                       showLine={this.props.def.showGroupDivider ?? true}
                       headerContent={this.renderAdditionalHeaderContent(auditTrailData)}
                       options={this.getFormGroupOptions()}
                       optionsDisabled={this.props.storage.data.disabled}
                       selectedOption={selectedOption}
                       isOptionsOpened={isMenuOpened}
                       onOptionsButtonToggle={this.handleOptionsButtonToggle}
                       onOptionClick={this.handleOptionClick}
                       noBottomMargin={!isGroupToggled}
                       isSmallBottomMargin={this.props.def.isSmallBottomMargin}>
                {this.props.def.rows &&
                    <FieldsWrapper style={{ width: "100%" }} isColumn passRef={this._ref}>
                        {this.renderFields(this.props.def.rows, true)}
                    </FieldsWrapper>
                }
                {this.props.def.lineItems && this.renderLineItems(this.props.def.lineItems)}
                {this.renderActions(visibleActions)}
            </FormGroup>
        );
    }
}
